import { useDeferredValue, useEffect, useMemo, useRef, useState } from 'react';
import { useQuery } from 'react-query';
import { contentAPI } from '../services/api';
import { useSiteSettings } from '../hooks/useContent';
import { usePageSeo } from '../hooks/usePageSeo';
import { buildBreadcrumbSchema, buildOrganizationSchema } from '../utils/seo';
import BlogCard from '../components/BlogCard';
import LoadingIndicator from '../components/LoadingIndicator';
import './Blogs.css';

const Blogs = () => {
  const { settings } = useSiteSettings();
  const publicBase =
    String(settings.site_public_url || '').replace(/\/$/, '') ||
    (typeof window !== 'undefined' ? window.location.origin : '');
  const brand = settings.site_brand_name || 'Invincible NGO';

  const blogsSchema = useMemo(() => {
    const org = buildOrganizationSchema(settings);
    const breadcrumbs = buildBreadcrumbSchema([
      { name: 'Trek Stories & Blogs', url: '/blogs' }
    ], publicBase);
    return {
      '@context': 'https://schema.org',
      '@graph': [org, breadcrumbs]
    };
  }, [settings, publicBase]);

  usePageSeo({
    title: `${settings.blogs_hero_title || 'Trek Stories & Blogs'} | ${brand}`,
    description:
      String(settings.blogs_hero_subtitle || '').trim() ||
      settings.site_meta_description,
    canonical: publicBase ? `${publicBase}/blogs` : undefined,
    schema: blogsSchema
  });

  const [search, setSearch] = useState('');
  const [category, setCategory] = useState('');
  const [categoryOpen, setCategoryOpen] = useState(false);
  const categoryWrapRef = useRef(null);
  const deferredSearch = useDeferredValue(search);

  const { data: blogs = [], isLoading: loading } = useQuery(
    ['blogs', 'index', { search: deferredSearch, category }],
    async () => {
      const res = await contentAPI.getBlogs({
        limit: 80,
        ...(deferredSearch.trim() ? { search: deferredSearch.trim() } : {}),
        ...(category.trim() ? { category: category.trim() } : {}),
      });
      const rows = res.data?.data;
      return Array.isArray(rows) ? rows : [];
    },
    { staleTime: 60 * 1000 }
  );

  const categories = useMemo(() => {
    const set = new Set();
    blogs.forEach((b) => {
      const c = b?.category && String(b.category).trim();
      if (c) set.add(c);
    });
    return Array.from(set).sort((a, b) => a.localeCompare(b));
  }, [blogs]);

  useEffect(() => {
    if (!categoryOpen) return undefined;
    const onDown = (e) => {
      if (e.key === 'Escape') setCategoryOpen(false);
    };
    const onClick = (e) => {
      const el = categoryWrapRef.current;
      if (!el) return;
      if (!el.contains(e.target)) setCategoryOpen(false);
    };
    window.addEventListener('keydown', onDown);
    window.addEventListener('mousedown', onClick);
    return () => {
      window.removeEventListener('keydown', onDown);
      window.removeEventListener('mousedown', onClick);
    };
  }, [categoryOpen]);

  const heroTitle = settings.blogs_hero_title || settings.blogs_section_title || 'Latest Blogs';
  const heroSubtitle = settings.blogs_hero_subtitle || settings.blogs_section_subtitle || 'Stories From The Trails';
  const heroImage = settings.blogs_hero_image || '';
  const heroClassName = heroImage ? 'blog-hero-banner blog-hero-banner-with-image' : 'blog-hero-banner';

  return (
    <div className="blogs-page">
      <div
        className={heroClassName}
        style={
          heroImage
            ? {
                backgroundImage: `linear-gradient(135deg, rgba(15,23,42,0.9) 0%, rgba(15,23,42,0.75) 40%, rgba(15,23,42,0.55) 100%), url(${heroImage})`,
                backgroundSize: 'cover',
                backgroundPosition: 'center',
              }
            : undefined
        }
      >
        <div className="blog-hero-inner">
          <h1 className="blog-hero-title">{heroTitle}</h1>
          <p className="blog-hero-subtitle">{heroSubtitle}</p>
        </div>
      </div>

      <div className="blogs-content">
        <div className="blogs-toolbar">
          <label className="blogs-filter-label">
            <span className="blogs-filter-text">Search</span>
            <input
              type="search"
              className="blogs-filter-input"
              placeholder="Search by title…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              aria-label="Search blogs by title"
            />
          </label>
          {categories.length > 0 ? (
            <label className="blogs-filter-label">
              <span className="blogs-filter-text">Category</span>
              <div className="blogs-select-wrap" ref={categoryWrapRef}>
                <button
                  type="button"
                  className={`blogs-select-button ${categoryOpen ? 'is-open' : ''}`}
                  aria-haspopup="listbox"
                  aria-expanded={categoryOpen}
                  onClick={() => setCategoryOpen((v) => !v)}
                >
                  <span className="blogs-select-value">
                    {category.trim() ? category : 'All categories'}
                  </span>
                  <span className="blogs-select-chevron" aria-hidden="true" />
                </button>
                {categoryOpen ? (
                  <div className="blogs-select-popover" role="listbox" aria-label="Filter by category">
                    <button
                      type="button"
                      className={`blogs-select-option ${category === '' ? 'is-selected' : ''}`}
                      role="option"
                      aria-selected={category === ''}
                      onClick={() => {
                        setCategory('');
                        setCategoryOpen(false);
                      }}
                    >
                      All categories
                    </button>
                    {categories.map((c) => (
                      <button
                        key={c}
                        type="button"
                        className={`blogs-select-option ${category === c ? 'is-selected' : ''}`}
                        role="option"
                        aria-selected={category === c}
                        onClick={() => {
                          setCategory(c);
                          setCategoryOpen(false);
                        }}
                      >
                        {c}
                      </button>
                    ))}
                  </div>
                ) : null}
              </div>
            </label>
          ) : null}
        </div>

        {loading ? (
          <div className="loading-state">
            <LoadingIndicator label="Loading blogs…" />
          </div>
        ) : blogs.length === 0 ? (
          <div className="empty-state">
            <p>No blogs match your filters yet. Try clearing search or category.</p>
          </div>
        ) : (
          <div className="blogs-grid">
            {blogs.map((blog) => {
              const blogDate = blog.publishedAt || blog.createdAt || blog.date;
              const formattedDate = blogDate
                ? new Date(blogDate).toLocaleDateString('en-US', {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric',
                  })
                : 'Recently';

              return (
                <BlogCard
                  key={blog.id}
                  to={`/blogs/${blog.slug || blog.id}`}
                  image={
                    blog.imageUrl ||
                    'https://images.unsplash.com/photo-1506905925346-21bda4d32df4?w=400'
                  }
                  category={(blog.category || 'BLOG').toUpperCase()}
                  title={blog.title}
                  date={formattedDate}
                  author={blog.author || 'Admin'}
                  excerpt={blog.shortDescription ? String(blog.shortDescription).trim() : ''}
                />
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default Blogs;
